import type { FaixaRanking } from './dashboard'
import type { StatusCampanha } from './campanha'

/*
 * Ranking com escopo (AN-8 §5, DEV-O). Contrato conferido na integração da Onda 4 contra o
 * `RankingController`/`RankingAdminResponse`/`RankingConfiguracaoResponse` do backend.
 */

/** Escopo do ranking (singleton `ranking_configuracao`, V56; Q-AN8-7: único e global). */
export type EscopoRanking = 'GERAL' | 'CANAL' | 'EMPRESA' | 'EQUIPE' | 'CAMPANHA'

export const ESCOPOS_RANKING: EscopoRanking[] = ['GERAL', 'CANAL', 'EMPRESA', 'EQUIPE', 'CAMPANHA']

export const ESCOPO_RANKING_ROTULO: Record<EscopoRanking, string> = {
  GERAL: 'Geral',
  CANAL: 'Por canal',
  EMPRESA: 'Por empresa',
  EQUIPE: 'Por equipe',
  CAMPANHA: 'Por campanha',
}

/** O que o consultor passa a ver no card "Seu ranking" com cada escopo. */
export const ESCOPO_RANKING_DESCRICAO: Record<EscopoRanking, string> = {
  GERAL: 'Cada consultor vê a própria posição entre todos os consultores',
  CANAL: 'Cada consultor vê a própria posição entre os consultores do seu canal',
  EMPRESA: 'Cada consultor vê a própria posição entre os consultores da sua empresa',
  EQUIPE: 'Cada consultor vê a própria posição na sua equipe (sem equipe: ranking indisponível)',
  CAMPANHA: 'Cada participante vê a própria posição na campanha escolhida (fora dela: indisponível)',
}

/**
 * `GET /v1/dashboard/ranking/configuracao` — back-office (OPERADOR+); quem tem escopo de
 * consultor (inclusive com OPERADOR residual) → 403 `RANKING_SO_BACKOFFICE`. `PUT` só ADMIN.
 * `campanha*` só com `escopo = CAMPANHA` (nulos nos demais).
 */
export interface RankingConfiguracao {
  escopo: EscopoRanking
  campanhaId: string | null
  campanhaNome: string | null
  campanhaStatus: StatusCampanha | null
  atualizadoPor: string | null
  atualizadoEm: string | null
}

/** Corpo do `PUT .../configuracao` (ADMIN). `campanhaId` só com CAMPANHA, senão `null`. */
export interface RankingConfiguracaoRequest {
  escopo: EscopoRanking
  campanhaId: string | null
}

export type PeriodoRanking = 'MENSAL' | 'ANUAL'

/**
 * `GET /v1/dashboard/ranking?periodo=&referencia=&escopo=&canal=&tenantId=&equipeId=
 * &campanhaId=&page=&size=` (GESTAO_CAMPANHA_EQUIPE). ADMIN: qualquer escopo. GERENTE+GESTOR:
 * qualquer escopo, mas só as linhas da própria equipe (posição do universo inteiro); `equipeId`
 * de outra equipe → 403 `RANKING_FORA_DA_EQUIPE`. Sem `escopo`, vale o configurado.
 */
export interface RankingAdminFiltro {
  periodo: PeriodoRanking
  /** Data ISO (YYYY-MM-DD) dentro do período; ausente = período corrente. */
  referencia?: string
  escopo?: EscopoRanking
  canal?: string
  tenantId?: string
  equipeId?: string
  campanhaId?: string
  page?: number
  /** Máximo 200 (acima → 400 `RANKING_PAGINACAO_INVALIDA`). */
  size?: number
}

export interface EquipeRef {
  id: string
  nome: string
}

export interface RankingAdminLinha {
  /** Competição (1, 2, 2, 4) no universo do escopo; null com volume zero. */
  posicao: number | null
  consultorId: string
  nome: string | null
  canal: string | null
  tenantId: string | null
  equipe: EquipeRef | null
  volume: number
  quantidade: number
  faixa: FaixaRanking | null
}

export interface RankingAdmin {
  escopo: EscopoRanking
  canal: string | null
  tenantId: string | null
  equipeId: string | null
  campanhaId: string | null
  periodo: PeriodoRanking
  inicio: string
  fim: string
  rotuloPeriodo: string
  /** Tamanho do universo do escopo (base da posição e da faixa) — não da paginação. */
  totalNoUniverso: number
  /** `totalNoUniverso >= 10`: com menos, o card do consultor diz "indisponível" (Q28). */
  disponivelParaConsultor: boolean
  /** GERENTE+GESTOR: linhas só da equipe dele, posição do universo inteiro (sem renumerar). */
  recorteEquipe: boolean
  /** Linhas depois do recorte — base da paginação. */
  totalLinhas: number
  page: number
  size: number
  totalPaginas: number
  linhas: RankingAdminLinha[]
}

/** Erros do ranking (`errorCode` do backend) em texto legível. */
export const ERRO_RANKING_ROTULO: Record<string, string> = {
  RANKING_SO_BACKOFFICE: 'A configuração do ranking é do back-office.',
  RANKING_ESCOPO_OBRIGATORIO: 'Escolha o escopo do ranking.',
  RANKING_CAMPANHA_OBRIGATORIA: 'Escolha a campanha do ranking.',
  RANKING_CAMPANHA_SO_NO_ESCOPO_CAMPANHA: 'A campanha só vale com o escopo "Por campanha".',
  RANKING_CAMPANHA_NAO_ENCONTRADA: 'A campanha escolhida não existe mais.',
  RANKING_CAMPANHA_ENCERRADA: 'A campanha escolhida está encerrada — escolha uma campanha ativa.',
  RANKING_PERIODO_INVALIDO: 'Período inválido: use mensal ou anual.',
  RANKING_ESCOPO_INVALIDO: 'Escopo do ranking inválido.',
  RANKING_PAGINACAO_INVALIDA: 'Paginação inválida.',
  RANKING_CANAL_OBRIGATORIO: 'Escolha o canal para ver o ranking.',
  RANKING_EMPRESA_OBRIGATORIA: 'Escolha a empresa para ver o ranking.',
  RANKING_EQUIPE_OBRIGATORIA: 'Escolha a equipe para ver o ranking.',
  RANKING_EQUIPE_NAO_ENCONTRADA: 'A equipe escolhida não existe.',
  RANKING_FORA_DA_EQUIPE: 'Você só pode ver o ranking da sua equipe.',
  GESTAO_SEM_PERMISSAO: 'Você não tem permissão para ver este ranking.',
}

/** Mensagem legível de um erro rejeitado pelo `api` (`{ message, status, data: { errorCode } }`). */
export const mensagemErroRanking = (e: unknown, padrao: string): string => {
  const env = e as { message?: string; data?: { errorCode?: string; message?: string } } | undefined
  const codigo = env?.data?.errorCode
  return (codigo && ERRO_RANKING_ROTULO[codigo]) || env?.data?.message || env?.message || padrao
}

/** Valor da trilha `RANKING_ESCOPO`: "GERAL" ou "CAMPANHA:<uuid>" → texto. */
export const rotuloValorEscopo = (valor?: string | null): string => {
  if (!valor) return '—'
  const [escopo, campanhaId] = valor.split(':')
  const rotulo = ESCOPO_RANKING_ROTULO[escopo as EscopoRanking] ?? escopo
  return campanhaId ? `${rotulo} (${campanhaId.slice(0, 8)}…)` : rotulo
}

export const FAIXA_RANKING_ROTULO: Record<FaixaRanking, string> = {
  TOP_10: '10% primeiros',
  TOP_25: '25% primeiros',
  TOP_50: '50% primeiros',
  DEMAIS: 'Demais',
}
